import React, { useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
	Box,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	ListSubheader,
	TextField,
	Tooltip,
	Checkbox,
	ListItemText,
	Typography,
	Paper,
	InputAdornment,
	Grid,
} from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { KeyboardArrowDown, Clear as ClearIcon, Search as SearchIcon, SyncAlt as SyncAltIcon } from "@material-ui/icons"
import "./ToolbarDropdown.css"

const useStyles = makeStyles((theme) => ({
	header: {
		position: "sticky",
		top: 0,
		zIndex: 2,
	},
	card: {
		width: "100%",
	},
	search: {
		"& .MuiFilledInput-root": {
			border: `1px solid ${theme.palette.grey[50]}`,
			borderRadius: "3px",
			background: theme.palette.grey[50],
		},
		"& .MuiInputBase-root": {
			width: "95%",
			height: "36px",
			boxShadow: "none",
			borderRadius: "3px 3px 0px 0px",
			border: "1px solid rgba(44, 53, 51, 0.12)",
			margin: "10px 0px 0px 12px",
		},
		"& .MuiInputBase-input": {
			boxShadow: "none",
			border: "none",
		},
		"& .MuiInputBase-input:focus": {
			boxShadow: "none",
			border: "none",
		},
	},
	select: {
		"& .MuiSvgIcon-root": {
			color: theme.palette.text.primary,
		},
		"& .MuiSelect-root": {
			fontWeight: 400,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "none",
		},
		"& .MuiListItem-root": {
			border: "solid 1px red",
		},

		"& .MuiSelect-root:hover": {
			backgroundColor: theme.palette.primary.dark,
		},

		"& .MuiSelect-root:hover .textColor": {
			color: `${theme.palette.primary.contrastText}`,
		},

		"& .MuiSelect-select:focus": {
			background: "none",
		},
	},
	formControlSelect: {
		"&:hover .input-label-concepts": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"& .input-label-concepts.Mui-focused": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"&:hover .MuiInputBase-root .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"& .MuiInputBase-root.Mui-focused .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"&.selected-icon-color .MuiInputBase-root .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		// "& .MuiFormControl-root:hover .MuiFormLabel-root.input-label-concepts": {
		// 	color: `${theme.palette.primary.contrastText}`,
		// },
		"& .MuiInputBase-root.MuiOutlinedInput-root.qa-Shared.Concepts-select-dropdown.Mui-focused": {
			background: theme.palette.primary.dark,
		},
		"& .MuiInputBase-root.MuiOutlinedInput-root.qa-Your.Concepts-select-dropdown.Mui-focused": {
			background: theme.palette.primary.dark,
		},
	},
	searchIcon: {
		marginLeft: "10px",
	},
	alphabeticalHeader: {
		...theme.typography.body2,
		fontWeight: 500,
		height: "32px",
		marginTop: "15px",
		color: theme.palette.grey["D300"],
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		paddingBottom: "15px",
		paddingRight: "22px",
		borderBottom: "solid 1px rgba(44, 53, 51, 0.12)",
	},
	conceptCheckbox: {
		padding: "3px 12px 0px 0px",
	},
}))

const containsText = (data, searchText, nameField) => data?.[nameField]?.toLowerCase()?.indexOf(searchText.toLowerCase()) > -1

const MenuProps = {
	PaperProps: {
		style: {
			maxWidth: 486,
			minWidth: 486,
			maxHeight: 506,
			minHeight: 506,
			boxShadow: "none",
			border: "1px solid rgba(44, 53, 51, 0.12)",
		},
	},
	anchorOrigin: {
		vertical: "bottom",
		horizontal: "left",
	},
	getContentAnchorEl: null,
}

// LOAD CONCEPTS SORT
const sortAZ = (property) => {
	return function (a, b) {
		if (a?.[property]?.toLowerCase() < b?.[property]?.toLowerCase()) {
			return -1
		} else if (a?.[property]?.toLowerCase() > b?.[property]?.toLowerCase()) {
			return 1
		} else {
			return 0
		}
	}
}

function sortZA(property) {
	return function (a, b) {
		if (a[property].toLowerCase() > b[property].toLowerCase()) {
			return -1
		} else if (a[property].toLowerCase() < b[property].toLowerCase()) {
			return 1
		} else {
			return 0
		}
	}
}

const sortData = (fieldName = "description", data = [], sortType = { alphabetical: "asc", date: "" }) => {
	const filterData = [...data]
	if (filterData?.length > 0) {
		try {
			if (sortType?.alphabetical === "asc") {
				filterData.sort(sortAZ([fieldName]))
			}

			if (sortType?.alphabetical === "desc") {
				filterData.sort(sortZA([fieldName]))
			}

			if (sortType?.date === "asc") {
				const futureDate = new Date(8640000000000000)
				filterData.sort((a, b) => {
					return moment
						.utc(a?.creation_time ? a?.creation_time : futureDate)
						.diff(moment.utc(b?.creation_time ? b?.creation_time : futureDate))
				})
			}

			if (sortType?.date === "desc") {
				const pastDate = new Date(-8640000000000000)
				filterData.sort((a, b) => {
					return moment
						.utc(b?.creation_time ? b?.creation_time : pastDate)
						.diff(moment.utc(a?.creation_time ? a?.creation_time : pastDate))
				})
			}
		} catch (e) {
			//console.log(e);
		}
	}
	return filterData
}

const ConceptDropdown = ({
	label = "Email",
	handleListChange,
	isLoading = false,
	datalist = [],
	boxStyle = { maxWidth: 160, minWidth: 160 },
	setSortConcepts = () => {},
	toolbarType = "your_concepts",
	nameField = "description",
	countField = "doc_count",
	activeTab = "your_concepts",
	setActiveTab = () => {},
	selected = "",
	setSelected = () => {},
	disabled = false,
}) => {
	const [sortType, setSortType] = useState({
		alphabetical: "asc",
		date: "",
	})
	const [list, setList] = useState([])
	const [t] = useTranslation("global")
	const classes = useStyles()
	const theme = useTheme()
	//const [selected, setSelected] = useState([])
	const [searchText, setSearchText] = useState("")
	const alphabeticalOrderedfilteredData = useMemo(
		() => list?.filter((option) => containsText(option, searchText, nameField)),
		[list, searchText]
	)

	const handleChange = (event) => {
		setSelected(event.target.value)
		//if (event.target.value?.length > 0) {
		setActiveTab(toolbarType)
		handleListChange(toolbarType, event.target.value)
		//handleListChange(list.find((item) => item?.id === selected))
		//}
	}

	// useEffect(() => {
	// 	if (selected?.length > 0) {
	// 		setActiveTab(toolbarType)
	// 		handleListChange(toolbarType, selected)
	// 		//handleListChange(list.find((item) => item?.id === selected))
	// 	}
	// }, [selected])

	useEffect(() => {
		setList((prevList) =>
			datalist?.length > 0 && (sortType.alphabetical || sortType.date) ? [...sortData(nameField, datalist, sortType)] : []
		)
	}, [datalist, sortType])

	// useEffect(() => {
	// 	if (activeTab !== "shared_concepts" || activeTab !== "your_concepts") {
	// 		setSelected("")
	// 	}
	// }, [activeTab])

	const handleClear = () => {
		setSearchText("")
		setSortType((prevData) => ({
			alphabetical: "asc",
			date: "",
		}))
	}

	const formatDate = (date) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "2-digit",
		})
	}

	const handleSortChange = (type, e) => {
		e.stopPropagation()
		setSortType((prevData) => ({
			...(type === "alphabetical" ? { date: "" } : { alphabetical: "" }),
			[type]: prevData?.[type] === "desc" ? "asc" : "desc",
		}))
	}

	const selectedObj = []
	selected?.length > 0 &&
		selected?.map((dataItem) => {
			let listItem = list?.find((item) => item?.id === dataItem)
			if (listItem && Object.keys(listItem)?.length > 0) {
				selectedObj.push(listItem?.description)
			}
			return selectedObj
		})

	return (
		<Box style={{ ...boxStyle }} m={1}>
			<FormControl
				fullWidth
				size="small"
				variant="outlined"
				className={`${classes.formControlSelect} ${activeTab === toolbarType ? "selected-icon-color" : ""}`}
			>
				<InputLabel
					style={{
						fontWeight: theme.typography.fontWeightMedium,
						...(activeTab === toolbarType ? { color: theme.palette.primary.contrastText } : {}),
					}}
					id={`${label}-input-label-text-dropdown`}
					shrink={false}
					className={`input-label-concepts ${toolbarType}#input-label-concepts`}
				>
					{label}
				</InputLabel>
				{/* <Tooltip placement="top" arrow title={selected?.length > 0 ? selectedObj.join(", ") : ""}> */}
				<Select
					variant="outlined"
					MenuProps={MenuProps}
					labelId={`${label}-select-label-input`}
					id={`${label}-search-select`}
					multiple
					value={[...selected]}
					//renderValue={(_) => (selected?.length > 0 ? selectedObj.join(", ") : "")}
					renderValue={(_) => null}
					label="Options"
					onChange={handleChange}
					onClose={handleClear}
					disabled={disabled}
					style={{
						...(activeTab === toolbarType
							? { backgroundColor: theme.palette.primary.dark }
							: {
									//color: theme.palette.text.primary
							  }),
					}}
					IconComponent={KeyboardArrowDown}
					className={`qa-${label}-select-dropdown ${toolbarType}#concepts-dropdown ${classes.select}`}
					displayEmpty
				>
					{isLoading &&
						Array(9)
							.fill()
							.map((data, i) => (
								<MenuItem key={i} value={""}>
									<Skeleton variant="rounded" width={486} height={41} />
								</MenuItem>
							))}
					{!isLoading && list.length > 0 && (
						<div className={classes.header}>
							<Paper className={classes.card} elevation={0}>
								<TextField
									className={`qa-${label}-search-field ${classes.search} textColor`}
									size="small"
									autoFocus
									placeholder="Search"
									fullWidth
									onChange={(e) => setSearchText(e.target.value)}
									onKeyDown={(e) => {
										if (e.key !== "Escape") {
											e.stopPropagation()
										}
									}}
									margin="none"
									variant="standard"
									InputProps={{
										disableUnderline: true,
										startAdornment: (
											<InputAdornment className={`${classes.searchIcon}`} position="start">
												<SearchIcon style={{ width: "16px", height: "16px", color: theme.palette.text.primary }} />
											</InputAdornment>
										),
									}}
								/>
							</Paper>
						</div>
					)}
					{!isLoading && list.length <= 0 && (
						<ListItemText className="no-data-found-item" primary={t("filterConceptDropdown.noDataFound")} />
					)}

					{!isLoading && list.length > 0 && (
						<ListSubheader className={classes.alphabeticalHeader}>
							<Grid item>
								<div onClick={(e) => handleSortChange("alphabetical", e)}>
									<Grid
										container
										direction="row"
										alignItems="center"
										justifyContent="center"
										style={{ cursor: "pointer" }}
									>
										<Grid item>{t("filterConceptDropdown.alphabetical")}</Grid>
										<SyncAltIcon style={{ transform: "rotate(90deg)", width: "15px", height: "15px" }} />
									</Grid>
								</div>
							</Grid>
							<Grid item>
								<div onClick={(e) => handleSortChange("date", e)}>
									<Grid
										container
										direction="row"
										alignItems="center"
										justifyContent="center"
										style={{ cursor: "pointer" }}
									>
										<Grid item style={{ fontWeight: 400, fontSize: 12, color: theme.palette.grey["D300"] }}>
											{t("filterConceptDropdown.date")}
										</Grid>
										<SyncAltIcon style={{ transform: "rotate(90deg)", width: "15px", height: "15px" }} />
									</Grid>
								</div>
							</Grid>
						</ListSubheader>
					)}
					{!isLoading &&
						list.length > 0 &&
						alphabeticalOrderedfilteredData.map((option, i) => (
							<MenuItem
								key={option?.id}
								value={option?.id}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									borderBottom: "solid 1px rgba(44, 53, 51, 0.12)",
									paddingRight: "22px",
								}}
								className={`qa-${label}-alphabetical-menu-item-${i}`}
							>
								<Grid item style={{ display: "flex", alignItems: "flex-start" }}>
									<Checkbox checked={selected.indexOf(option?.id) > -1} className={classes.conceptCheckbox} />
									<Grid>
										<Typography
											style={{ ...theme.typography.body2, fontWeight: 400 }}
											nowrap
											className="typography-ellipsis"
										>
											{option?.[nameField]}
										</Typography>

										<Typography
											style={{ ...theme.typography.body2, fontWeight: 400, color: theme.palette.grey["D300"] }}
											nowrap
											className="typography-ellipsis"
										>
											{option[countField] >= 0 ? option[countField] : "—"}
										</Typography>
									</Grid>
								</Grid>
								<Grid item style={{ alignSelf: "start" }}>
									<Typography style={{ fontSize: 11 }} nowrap className="typography-ellipsis">
										{option?.creation_time ? formatDate(option?.creation_time) : "—"}
									</Typography>
								</Grid>
							</MenuItem>
						))}
				</Select>
				{/* </Tooltip> */}
			</FormControl>
		</Box>
	)
}

export default ConceptDropdown
